// * pusher
import Pusher from "pusher-js";

// * types
import { Channel } from "pusher-js";

/**
 * Custom hook for managing a Pusher instance with the provided key and cluster.
 *
 * @function
 * @name usePusher
 *
 * @param {string} key - The Pusher API key.
 * @param {string} cluster - The Pusher cluster.
 *
 * @returns {{
 *   pusher: { value: Pusher | undefined };
 *   channel: { value: Channel | undefined };
 *   initPusher: () => void;
 *   abortPusher: () => void;
 *   initSubscription: (subsKey: string) => void;
 * }}
 *
 * @throws {Error} If Pusher initialization fails.
 *
 * @example
 * const {
 *   pusher,
 *   channel,
 *   initPusher,
 *   abortPusher,
 *   initSubscription,
 * } = usePusher("yourKey", "yourCluster");
 *
 * // Initialize Pusher
 * initPusher();
 *
 * // Subscribe to a channel
 * initSubscription("yourChannel");
 *
 * // Abort and clean up Pusher connection
 * abortPusher();
 */
export const usePusher = (key: string, cluster: string) => {
    /**
     * State for the Pusher instance.
     */
    const pusher = useState<Pusher | undefined>("pusher", () => undefined);

    /**
     * State for the Pusher channel.
     */
    const channel = useState<Channel | undefined>("channel", () => undefined);

    /**
     * Initializes the Pusher instance with the provided key and cluster.
     *
     * @function
     * @name initPusher
     * @returns {void}
     */
    const initPusher = (): void => {
        /**
         * The Pusher instance.
         * @type {Pusher | undefined}
         */
        pusher.value = new Pusher(key, { cluster: cluster });
    };

    /**
     * Initializes a subscription to a Pusher channel with the specified key.
     *
     * @function
     * @name initSubscription
     * @param {string} subsKey - The key of the Pusher channel to subscribe to.
     * @returns {void}
     */
    const initSubscription = (subsKey: string): void => {
        /**
         * Connects to the Pusher server if not already connected.
         * @type {Pusher | null}
         */
        pusher.value?.connect();

        /**
         * The Pusher channel object for the specified key.
         * @type {Channel | undefined}
         */
        channel.value = pusher.value?.subscribe(subsKey);
    };

    /**
     * Aborts and cleans up the Pusher connection.
     *
     * @function
     * @name abortPusher
     * @returns {void}
     */
    const abortPusher = (): void => {
        pusher.value?.unsubscribe(channel.value?.name as string);
        channel.value?.disconnect();
        pusher.value?.disconnect();
        pusher.value?.unbind_all();
        channel.value = undefined;
    };

    return {
        pusher,
        channel,
        initPusher,
        abortPusher,
        initSubscription,
    };
};
